import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useCurrentUserHasFeatureFlag, useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isPdfSettingsEnabled = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_NEW_SHORTLIST_REPORT);
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;

	const isAccountSettingsActive = currentPath.includes("account-settings");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");

	const showRfxSettings = (hasRfxSettings && isProcurer) || !isProcurer;
	const hasChildren = showRfxSettings || isProcurer;

	if (!hasChildren) {
		return (
			<AppMenuItem
				name="Settings"
				icon={<FiSettings size={24} />}
				link={accountSettingsLink}
				isCollapsed={isCollapsed}
				testId="account-settings"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
		);
	}

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={accountSettingsLink}
			testId="settings-menu"
			tourName="AccountSettings"
			onNavigate={onNavigate}
		>
			<ExpandableMenuChildItem
				name="Account Settings"
				link={accountSettingsLink}
				isActive={isAccountSettingsActive}
				testId="account-settings-child"
				onNavigate={onNavigate}
			/>
			{showRfxSettings && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{isProcurer && isPdfSettingsEnabled && (
				<ExpandableMenuChildItem
					name="PDF Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
